import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import { Loader2, ArrowLeft, Trash2, RefreshCw } from 'lucide-react';

export function AdminDeletedLog() {
  const navigate = useNavigate();
  const { data: logs, isLoading } = useQuery({
    queryKey: ['adminDeletedIdeasLog'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/ideas/deleted-log');
      return res.data.data;
    },
  });

  const queryClient = useQueryClient();
  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiClient.post(`/admin/ideas/${id}/restore`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminDeletedIdeasLog'] });
      window.alert('Idea restored successfully');
    },
    onError: (err: any) => {
      window.alert(err.response?.data?.error?.message || 'Failed to restore idea');
    }
  });

  return (
    <Layout title="DOM680 Portal">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <div className="flex items-center space-x-2 text-slate-500 text-sm mb-1">
            <Link to="/admin" className="hover:text-blue-600 transition-colors flex items-center">
              <ArrowLeft className="w-3.5 h-3.5 mr-1" />
              Back to Dashboard
            </Link>
          </div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center">
            <Trash2 className="w-5 h-5 mr-2 text-slate-400" />
            Deleted Ideas Log
          </h2>
          <p className="text-slate-500 text-sm">History of ideas deleted by administrators</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col mb-8">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50/90 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">#</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Ticket ID</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Title & Author</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Deleted By</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Reason</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Deleted On</th>
                  <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {!logs || logs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500 text-sm">
                      No deleted ideas found.
                    </td>
                  </tr>
                ) : (
                  logs.map((idea: any, index: number) => {
                    const deleteAction = idea.timelines?.[0];
                    return (
                      <tr
                        key={idea.id}
                        className="hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/ideas/${idea.id}`)}
                      >
                        <td className="px-6 py-4 align-middle text-sm font-semibold text-slate-600">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4 align-middle">
                          <span className="font-semibold text-slate-500 text-xs whitespace-nowrap uppercase">
                            CG{idea.id.substring(0, 7)}
                          </span>
                        </td>
                        <td className="px-6 py-4 align-middle">
                          <div className="min-w-[200px]">
                            <p className="font-semibold text-slate-800 text-sm">{idea.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5">by {idea.employee?.name}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 align-middle">
                          <span className="text-sm text-slate-700 font-medium">
                            {deleteAction?.actor?.name || 'Unknown Admin'}
                          </span>
                        </td>
                        <td className="px-6 py-4 align-middle">
                          <p className="text-sm text-slate-600 max-w-sm">
                            {deleteAction?.comment || '-'}
                          </p>
                        </td>
                        <td className="px-6 py-4 align-middle">
                          <span className="text-sm text-slate-500">
                            {new Date(idea.deletedAt).toLocaleString('en-GB')}
                          </span>
                        </td>
                        <td className="px-6 py-4 align-middle text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('Are you sure you want to restore this idea?')) {
                                restoreMutation.mutate(idea.id);
                              }
                            }}
                            disabled={restoreMutation.isPending}
                            className="bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700 px-3 py-1.5 rounded-lg font-bold text-xs inline-flex items-center transition-colors border border-emerald-200"
                          >
                            <RefreshCw className="w-3 h-3 mr-1" /> Restore
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
